import type { ComplianceCalendarItem, PenaltyItem } from "@services/facilityCompliance";
import type { DashboardPenaltyExposureTile } from "@services/facilityDashboard";
import { PENALTY_EXPOSURE_PENDING_DISPLAY } from "@constants/dashboardPenaltyExposure";
import { isTaxComplianceCalendarItem } from "@utils/penaltyCalendarTax";
import {
  allPenaltyExposureProgressStepsComplete,
  type PenaltyExposureProgressStep,
} from "./penaltyExposureProgressSteps";

export { PENALTY_EXPOSURE_PENDING_DISPLAY };

function complianceCalendarDeadlineIso(deadline: string): string | null {
  const trimmed = deadline.trim();
  if (!trimmed || trimmed === "TBD") return null;
  const head = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  return head ? head[1] : null;
}

function isOngoingCalendarItem(
  item?: { cadence?: string; deadlineType?: string },
): boolean {
  return item?.cadence === "ongoing" || item?.deadlineType === "none";
}

function isDateInQuarter(
  deadlineStr: string,
  quarter: { start: string; end: string },
): boolean {
  const iso = complianceCalendarDeadlineIso(deadlineStr);
  if (!iso) return false;
  return iso >= quarter.start && iso <= quarter.end;
}

function parseExposureAmount(raw: string | number | null | undefined): number {
  if (raw == null) return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.max(0, raw);
  const s = String(raw).trim();
  if (!s) return 0;
  const amounts: number[] = [];
  const re = /\d[\d,]*(?:\.\d+)?/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(s.replace(/,/g, ""))) !== null) {
    const val = Number(match[0]);
    if (Number.isFinite(val) && val > 0) amounts.push(val);
  }
  return amounts.length ? Math.max(...amounts) : 0;
}

/** Calendar quarter window; offset 0 = quarter containing asOfDate, -1 = prior quarter. */
export function getQuarterBoundsFromDate(
  asOfDateIso: string,
  offset: number,
): { start: string; end: string } | null {
  const parts = asOfDateIso.match(/^(\d{4})-(\d{2})/);
  if (!parts) return null;
  let year = parseInt(parts[1], 10);
  const month = parseInt(parts[2], 10);
  let q = Math.ceil(month / 3) + offset;
  while (q > 4) {
    q -= 4;
    year += 1;
  }
  while (q < 1) {
    q += 4;
    year -= 1;
  }
  const startMonth = (q - 1) * 3 + 1;
  const endMonth = q * 3;
  const start = `${year}-${String(startMonth).padStart(2, "0")}-01`;
  const endLastDay = new Date(year, endMonth, 0).getDate();
  const end = `${year}-${String(endMonth).padStart(2, "0")}-${String(endLastDay).padStart(2, "0")}`;
  return { start, end };
}

function calendarItemExposure(item: ComplianceCalendarItem): number {
  const max = (item as { penaltyMaxAmount?: number }).penaltyMaxAmount;
  if (typeof max === "number" && max > 0) return max;
  const min = (item as { penaltyMinAmount?: number }).penaltyMinAmount;
  if (typeof min === "number" && min > 0) return min;
  return parseExposureAmount(item.estimatedCostExposure);
}

export function sumComplianceCalendarQuarterExposure(
  items: ComplianceCalendarItem[] | undefined | null,
  quarter: { start: string; end: string },
): number {
  if (!items?.length) return 0;
  let total = 0;
  for (const item of items) {
    if (isTaxComplianceCalendarItem(item)) continue;
    if (isOngoingCalendarItem(item)) continue;
    if (!isDateInQuarter(item.deadline, quarter)) continue;
    total += calendarItemExposure(item);
  }
  return total;
}

function sumOngoingCalendarExposure(
  items: ComplianceCalendarItem[] | undefined | null,
): number {
  if (!items?.length) return 0;
  let total = 0;
  for (const item of items) {
    if (isTaxComplianceCalendarItem(item)) continue;
    if (!isOngoingCalendarItem(item)) continue;
    total += calendarItemExposure(item);
  }
  return total;
}

export function penaltyItemExposureAmount(item: PenaltyItem): number {
  const amount = item.workedExample?.result?.amount;
  if (typeof amount === "number" && Number.isFinite(amount) && amount > 0) {
    return amount;
  }
  return parseExposureAmount(amount);
}

export function sumPotentialPenaltyItemsExposure(
  items: PenaltyItem[] | undefined | null,
): number {
  if (!items?.length) return 0;
  return items.reduce((sum, item) => sum + penaltyItemExposureAmount(item), 0);
}

export function aggregatePenaltyExposureForQuarter(
  complianceCalendar: ComplianceCalendarItem[] | undefined | null,
  penaltyItems: PenaltyItem[] | undefined | null,
  quarter: { start: string; end: string },
): number {
  return (
    sumComplianceCalendarQuarterExposure(complianceCalendar, quarter) +
    sumOngoingCalendarExposure(complianceCalendar) +
    sumPotentialPenaltyItemsExposure(penaltyItems)
  );
}

function quarterLabelFromBounds(quarterStart: string): string {
  const year = parseInt(quarterStart.slice(0, 4), 10);
  const month = parseInt(quarterStart.slice(5, 7), 10);
  const q = Math.ceil(month / 3);
  return `Q${q} '${String(year).slice(-2)}`;
}

function buildQuarterSparkline(
  complianceCalendar: ComplianceCalendarItem[] | undefined | null,
  penaltyItems: PenaltyItem[] | undefined | null,
  asOfDateIso: string,
): DashboardPenaltyExposureTile["sparkline_points"] {
  const points: DashboardPenaltyExposureTile["sparkline_points"] = [];
  for (let offset = -3; offset <= 0; offset += 1) {
    // Caller already validated asOfDateIso, so every offset parses.
    const quarter = getQuarterBoundsFromDate(asOfDateIso, offset) as {
      start: string;
      end: string;
    };
    points.push({
      label: quarterLabelFromBounds(quarter.start),
      value: aggregatePenaltyExposureForQuarter(
        complianceCalendar,
        penaltyItems,
        quarter,
      ),
    });
  }
  return points;
}

export function formatExposureUsd(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}k`;
  return `$${Math.round(amount).toLocaleString()}`;
}

/**
 * True while the compliance calendar may still replace a $0 dashboard API placeholder.
 * Non-zero API totals are shown immediately (calendar only refines when it has data).
 */
export function isPenaltyExposureTilePending(
  apiTile: DashboardPenaltyExposureTile,
  options: { awaitingComplianceCalendar: boolean },
): boolean {
  if (apiTile.exposure_calculated === true) return false;
  if (apiTile.total_exposure > 0) return false;
  if (apiTile.exposure_calculated === false) return true;
  if (apiTile.total_exposure_display === PENALTY_EXPOSURE_PENDING_DISPLAY) {
    return true;
  }
  return options.awaitingComplianceCalendar;
}

export function isPenaltyExposureCalculated(
  tile: DashboardPenaltyExposureTile,
): boolean {
  if (tile.exposure_calculated === true) return true;
  if (tile.exposure_calculated === false) return false;
  // Legacy payloads: only a positive total is trustworthy; $0 may still be a placeholder.
  return tile.total_exposure > 0;
}

/** Keep the checklist visible only while SSE progress steps are still running. */
export function shouldShowPenaltyExposureProgressList(
  steps: PenaltyExposureProgressStep[],
  penaltyExposureDisplayReady: boolean,
  complianceCalendarLoading: boolean,
): boolean {
  void complianceCalendarLoading;
  if (steps.length === 0) return false;
  if (penaltyExposureDisplayReady) return false;
  if (allPenaltyExposureProgressStepsComplete(steps)) return false;
  return true;
}

/** Show live progress while the dashboard BFF tile payload is still loading. */
export function canShowPenaltyExposureProgressDuringTileLoad(
  steps: PenaltyExposureProgressStep[],
  penaltyExposureDisplayReady: boolean,
  tileLoading: boolean,
  complianceCalendarLoading: boolean,
): boolean {
  return (
    tileLoading &&
    shouldShowPenaltyExposureProgressList(
      steps,
      penaltyExposureDisplayReady,
      complianceCalendarLoading,
    )
  );
}

/** Label for the dashboard tile once exposure is ready (never the stale BFF pending placeholder). */
export function resolvePenaltyExposureDisplayLabel(
  exposure: DashboardPenaltyExposureTile | null,
  displayReady: boolean,
): string {
  if (!displayReady || !exposure) return PENALTY_EXPOSURE_PENDING_DISPLAY;
  if (
    isPenaltyExposureTilePending(exposure, { awaitingComplianceCalendar: false })
  ) {
    return PENALTY_EXPOSURE_PENDING_DISPLAY;
  }
  const raw = exposure.total_exposure_display?.trim();
  if (raw && raw !== PENALTY_EXPOSURE_PENDING_DISPLAY) {
    return raw;
  }
  return formatExposureUsd(exposure.total_exposure ?? 0);
}

/**
 * True when the dashboard tile should show a dollar total instead of the pending checklist.
 * Covers BFF-calculated exposure and client-side aggregation after the SSE pipeline finishes.
 */
export function isPenaltyExposureDashboardReady(
  exposure: DashboardPenaltyExposureTile | null,
  options: {
    penaltyExposurePending: boolean;
    penaltiesDataReady: boolean;
    complianceCalendarLoading: boolean;
    progressPipelineComplete: boolean;
  },
): boolean {
  if (!exposure) return false;
  if (isPenaltyExposureCalculated(exposure) && !options.penaltyExposurePending) {
    return true;
  }
  if (
    options.progressPipelineComplete &&
    options.penaltiesDataReady &&
    isPenaltyExposureCalculated(exposure)
  ) {
    return true;
  }
  return false;
}

/** Align dashboard penalty tile with Potential Penalties accordions when BFF returns $0. */
export function resolvePenaltyExposureFromPenaltiesData(
  apiTile: DashboardPenaltyExposureTile,
  complianceCalendar: ComplianceCalendarItem[] | undefined | null,
  penaltyItems: PenaltyItem[] | undefined | null,
  asOfDateIso: string,
): DashboardPenaltyExposureTile {
  if (!complianceCalendar?.length && !penaltyItems?.length) return apiTile;
  const curQuarter = getQuarterBoundsFromDate(asOfDateIso, 0);
  const prevQuarter = getQuarterBoundsFromDate(asOfDateIso, -1);
  if (!curQuarter || !prevQuarter) return apiTile;

  // SSOT-38: RHC/FQHC Medicare remedies are denial/termination, not dollar CMPs.
  // The API already strips Part 491 calendar rows; Potential Penalties items stay out.
  const exposureItems =
    apiTile.enforcement_regime === "denial_of_payment_termination" ? [] : penaltyItems;

  // SSOT-61: prefer live Potential Penalties totals over a stale high BFF tile.
  const cur = aggregatePenaltyExposureForQuarter(
    complianceCalendar,
    exposureItems,
    curQuarter,
  );
  const prev = aggregatePenaltyExposureForQuarter(
    complianceCalendar,
    exposureItems,
    prevQuarter,
  );

  const hasQuarterTrend = prev > 0;
  let delta = 0;
  let direction: "up" | "down" | "flat" = "flat";
  if (hasQuarterTrend) {
    delta = cur - prev;
    if (delta > 0) direction = "up";
    else if (delta < 0) direction = "down";
  }
  const sparkline_points = hasQuarterTrend
    ? buildQuarterSparkline(complianceCalendar, exposureItems, asOfDateIso)
    : [];

  return {
    ...apiTile,
    total_exposure: cur,
    total_exposure_display: formatExposureUsd(cur),
    exposure_calculated: true,
    quarter_delta: hasQuarterTrend ? delta : 0,
    quarter_delta_display: formatExposureUsd(Math.abs(delta)),
    quarter_delta_direction: direction,
    sparkline_points,
    has_quarter_trend: hasQuarterTrend,
  };
}

